import { QueryClient, queryOptions } from '@tanstack/react-query'

import { getConfig } from '@/app/config'
import { warn } from '@/utils/logger'

import { getLastCustomerId } from '../domain/auth0'

const LOGIN_V1_FLAG_PATH = '/api/user-service/api/ship-users/feature-flags/login-v1'
const LOOKUP_TIMEOUT_MS = 3_000
const LOOKUP_STALE_TIME_MS = 1000 * 60 * 5

/**
 * Unauthenticated lookup of the login v1 flag for one customer. `/login` has no token to
 * authenticate the user-profile-core flag search with, so user-service exposes this endpoint and
 * evaluates the flag for the customer id Ship stored at the previous login.
 */
const getLoginV1Flag = async (customerId: string): Promise<boolean> => {
  const url = `${getConfig().API_ENDPOINT ?? ''}${LOGIN_V1_FLAG_PATH}?customerId=${encodeURIComponent(customerId)}`
  const response = await fetch(url, { signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS) })

  if (!response.ok) {
    throw new Error(`Login v1 flag lookup responded with ${response.status}`)
  }

  const { enabled } = (await response.json()) as { enabled?: boolean }

  return enabled === true
}

export const loginV1FlagQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: ['loginV1Flag', customerId],
    queryFn: () => getLoginV1Flag(customerId),
    staleTime: LOOKUP_STALE_TIME_MS,
    // The caller is a route guard a visitor is waiting on, and loginv2 is a fine answer
    retry: false,
  })

/**
 * Resolves false rather than rejecting: a customer whose flag cannot be read gets loginv2, the
 * default login.
 */
export const isLoginV1EnabledForCustomer = async (customerId: string, queryClient?: QueryClient): Promise<boolean> => {
  try {
    return queryClient
      ? await queryClient.query({ ...loginV1FlagQueryOptions(customerId), staleTime: 'static' })
      : await getLoginV1Flag(customerId)
  } catch (error) {
    warn('Login v1 flag lookup failed, falling back to loginv2', error)
    return false
  }
}

export type LoginPath = '/loginv1' | '/loginv2'

/**
 * The one place that picks the login page, so /login and logout agree. Nothing stored means
 * nothing to evaluate the flag for — a visitor who has never logged in on this browser gets
 * loginv2, the default.
 */
export const resolveLoginPath = async (queryClient?: QueryClient): Promise<LoginPath> => {
  const customerId = getLastCustomerId()

  return customerId && (await isLoginV1EnabledForCustomer(customerId, queryClient)) ? '/loginv1' : '/loginv2'
}
